import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import { format } from 'date-fns';
import './Customers.css';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX
  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  } else {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

const buildAddressString = (street, city, state, zip) =>
  [street, city, state, zip].filter(Boolean).join(', ');

const copyAddress = (text) => {
  if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
};

const openInMaps = (text) => {
  window.open(`https://maps.google.com/?q=${encodeURIComponent(text)}`, '_blank');
};

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showAddReferral, setShowAddReferral] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    name: '', address: '', city: '', state: 'TX', zip: '', is_default: false
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'TX',
    zip: '',
    notes: '',
    recurring_enabled: false,
    recurring_frequency: 'monthly',
    recurring_interval_days: '',
    recurring_service_id: '',
    recurring_start_date: '',
    recurring_paused: false,
    referred_by_customer_id: ''
  });

  const [vehicleFormData, setVehicleFormData] = useState({
    year: '',
    make: '',
    model: '',
    color: '',
    license_plate: '',
    vehicle_type: 'sedan'
  });

  useEffect(() => {
    fetchCustomers();
    fetchServices();
  }, [search]);

  const fetchCustomers = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`/api/customers${params}`);
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await fetch('/api/services/grouped');
      const data = await response.json();
      // Combine packages and addons into one list
      const allServices = [...data.packages, ...data.addons];
      setServices(allServices);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    try {
      const response = await fetch(`/api/customers/${customerId}`);
      const data = await response.json();
      setCustomerDetail(data);
    } catch (error) {
      console.error('Error fetching customer detail:', error);
    }
  };

  const toggleCustomerExpand = async (customerId) => {
    setShowAddReferral(false);
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
      setCustomerDetail(null);
    } else {
      setExpandedCustomer(customerId);
      await fetchCustomerDetail(customerId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingCustomer
        ? `/api/customers/${editingCustomer.id}`
        : '/api/customers';
      const method = editingCustomer ? 'PUT' : 'POST';

      // Convert boolean values to integers for SQLite
      const dataToSend = {
        ...formData,
        recurring_enabled: formData.recurring_enabled ? 1 : 0,
        recurring_paused: formData.recurring_paused ? 1 : 0
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        setShowModal(false);
        setEditingCustomer(null);
        resetForm();
        fetchCustomers();
        if (expandedCustomer) {
          fetchCustomerDetail(expandedCustomer);
        }
      }
    } catch (error) {
      console.error('Error saving customer:', error);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    if (!customerDetail) return;

    try {
      const response = await fetch('/api/vehicles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...vehicleFormData,
          customer_id: customerDetail.id
        })
      });

      if (response.ok) {
        setShowVehicleModal(false);
        resetVehicleForm();
        fetchCustomerDetail(customerDetail.id);
        fetchCustomers();
      }
    } catch (error) {
      console.error('Error adding vehicle:', error);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      const response = await fetch(`/api/vehicles/${vehicleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchCustomerDetail(customerDetail.id);
        fetchCustomers();
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Move this customer to trash? Their appointments and invoices will also be moved. You can restore them from the Trash page.')) {
      return;
    }

    try {
      const response = await fetch(`/api/customers/${customerId}`, { method: 'DELETE' });
      if (response.ok) {
        setExpandedCustomer(null);
        setCustomerDetail(null);
        fetchCustomers();
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip: customer.zip || '',
      notes: customer.notes || '',
      recurring_enabled: customer.recurring_enabled === 1,
      recurring_frequency: customer.recurring_frequency || 'monthly',
      recurring_interval_days: customer.recurring_interval_days || '',
      recurring_service_id: customer.recurring_service_id || '',
      recurring_start_date: customer.recurring_start_date || '',
      recurring_paused: customer.recurring_paused === 1,
      referred_by_customer_id: customer.referred_by_customer_id ? String(customer.referred_by_customer_id) : ''
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formattedPhone });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleVehicleChange = (e) => {
    setVehicleFormData({ ...vehicleFormData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', email: '', phone: '',
      address: '', city: '', state: 'TX', zip: '', notes: '',
      recurring_enabled: false,
      recurring_frequency: 'monthly',
      recurring_interval_days: '',
      recurring_service_id: '',
      recurring_start_date: '',
      recurring_paused: false,
      referred_by_customer_id: ''
    });
  };

  const resetVehicleForm = () => {
    setVehicleFormData({
      year: '', make: '', model: '', color: '', license_plate: '', vehicle_type: 'sedan'
    });
  };

  const resetAddressForm = () => {
    setAddressForm({ name: '', address: '', city: '', state: 'TX', zip: '', is_default: false });
    setEditingAddress(null);
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!customerDetail) return;

    try {
      const url = editingAddress
        ? `/api/customers/${customerDetail.id}/addresses/${editingAddress.id}`
        : `/api/customers/${customerDetail.id}/addresses`;
      const method = editingAddress ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addressForm)
      });

      if (response.ok) {
        setShowAddressModal(false);
        resetAddressForm();
        fetchCustomerDetail(customerDetail.id);
      }
    } catch (error) {
      console.error('Error saving address:', error);
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      name: address.name,
      address: address.address,
      city: address.city || '',
      state: address.state || 'TX',
      zip: address.zip || '',
      is_default: address.is_default === 1
    });
    setShowAddressModal(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      const response = await fetch(`/api/customers/${customerDetail.id}/addresses/${addressId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchCustomerDetail(customerDetail.id);
      }
    } catch (error) {
      console.error('Error deleting address:', error);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const response = await fetch(`/api/customers/${customerDetail.id}/addresses/${addressId}/default`, {
        method: 'PATCH'
      });
      if (response.ok) {
        fetchCustomerDetail(customerDetail.id);
      }
    } catch (error) {
      console.error('Error setting default address:', error);
    }
  };

  const handleAddReferral = async (referredCustomerId) => {
    if (!referredCustomerId || !customerDetail) return;
    try {
      const response = await fetch(`/api/customers/${referredCustomerId}/referred-by`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referred_by_customer_id: customerDetail.id })
      });
      if (response.ok) {
        setShowAddReferral(false);
        fetchCustomerDetail(customerDetail.id);
        fetchCustomers();
      }
    } catch (error) {
      console.error('Error adding referral:', error);
    }
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <h1 className="page-header__title">Customers</h1>
        <div className="page-header__actions">
          <button className="btn btn--primary" onClick={() => { setEditingCustomer(null); resetForm(); setShowModal(true); }}>
            + Add Customer
          </button>
        </div>
      </div>

      <div className="customers-toolbar">
        <div className="customers-count">
          <span className="customers-count__label">Total Customers:</span>
          <span className="customers-count__value">{customers.length}</span>
        </div>
        <div className="search-bar">
          <svg className="search-bar__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="form-input search-bar__input"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : customers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h3 className="empty-state__title">No customers yet</h3>
          <p>Add your first customer to get started</p>
          <button className="btn btn--primary mt-md" onClick={() => setShowModal(true)}>
            + Add Customer
          </button>
        </div>
      ) : (
        <div className="customer-list">
          {customers.map((customer) => (
            <div key={customer.id} className="customer-card">
              <div
                className="customer-card__header"
                onClick={() => toggleCustomerExpand(customer.id)}
              >
                <div className="customer-card__info">
                  <h3 className="customer-card__name">
                    {customer.first_name} {customer.last_name}
                    {customer.referral_count >= 4 && (
                      <span className="customer-card__gift" title="4+ referrals booked — free detail owed">🎁</span>
                    )}
                  </h3>
                  <div className="customer-card__contact">
                    <span>{customer.phone}</span>
                    {customer.email && <span>{customer.email}</span>}
                  </div>
                </div>
                <div className="customer-card__stats">
                  <div className="customer-card__stat">
                    <span className="stat-value">{customer.vehicle_count}</span>
                    <span className="stat-label">Vehicles</span>
                  </div>
                  <div className="customer-card__stat">
                    <span className="stat-value">{customer.appointment_count}</span>
                    <span className="stat-label">Appointments</span>
                  </div>
                  <div className="customer-card__stat" title="Referrals who have booked an appointment">
                    <span className={`stat-value ${customer.referral_count >= 4 ? 'stat-value--referral-ready' : ''}`}>
                      {customer.referral_count >= 4 ? '🎁 ' : ''}{customer.referral_count}
                    </span>
                    <span className="stat-label">Referrals</span>
                  </div>
                  <svg
                    className={`customer-card__chevron ${expandedCustomer === customer.id ? 'expanded' : ''}`}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </div>

              {expandedCustomer === customer.id && customerDetail && (
                <div className="customer-card__expanded">
                  <div className="expanded-card__top-actions">
                    <button
                      className="btn btn--outline btn--small"
                      onClick={() => navigate(`/customers/${customerDetail.id}`)}
                    >
                      View Full Profile
                    </button>
                  </div>
                  <div className="customer-detail-grid">
                    {/* Customer Info Section */}
                    <div className="detail-section">
                      <div className="detail-section__header">
                        <h4>Contact Information</h4>
                        <div className="detail-section__actions">
                          <button
                            className="btn btn--secondary btn--small"
                            onClick={() => handleEditCustomer(customerDetail)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn--danger btn--small"
                            onClick={() => handleDeleteCustomer(customerDetail.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="detail-info">
                        <div className="detail-row">
                          <span className="detail-label">Phone</span>
                          <span className="detail-value">{customerDetail.phone}</span>
                        </div>
                        {customerDetail.email && (
                          <div className="detail-row">
                            <span className="detail-label">Email</span>
                            <span className="detail-value">{customerDetail.email}</span>
                          </div>
                        )}
                        {customerDetail.address && (
                          <div className="detail-row">
                            <span className="detail-label">Address</span>
                            <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span>
                                {customerDetail.address}
                                {customerDetail.city && `, ${customerDetail.city}`}
                                {customerDetail.state && `, ${customerDetail.state}`}
                                {customerDetail.zip && ` ${customerDetail.zip}`}
                              </span>
                              <button
                                className="btn btn--outline btn--small"
                                title="Copy address"
                                onClick={() => copyAddress(buildAddressString(customerDetail.address, customerDetail.city, customerDetail.state, customerDetail.zip))}
                                style={{ padding: '2px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                              >📋 Copy</button>
                              <button
                                className="btn btn--outline btn--small"
                                title="Open in Maps"
                                onClick={() => openInMaps(buildAddressString(customerDetail.address, customerDetail.city, customerDetail.state, customerDetail.zip))}
                                style={{ padding: '2px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                              >🗺️ Maps</button>
                            </span>
                          </div>
                        )}
                        {customerDetail.notes && (
                          <div className="detail-row">
                            <span className="detail-label">Notes</span>
                            <span className="detail-value">{customerDetail.notes}</span>
                          </div>
                        )}
                        {customerDetail.recurring_enabled === 1 && (
                          <div className="detail-row">
                            <span className="detail-label">Recurring</span>
                            <span className="detail-value">
                              <span className={`recurring-badge ${customerDetail.recurring_paused === 1 ? 'recurring-badge--paused' : ''}`}>
                                {customerDetail.recurring_paused === 1 ? 'Paused' : 'Active'} - {
                                  customerDetail.recurring_frequency === 'biweekly' ? 'Every 2 Weeks' :
                                  customerDetail.recurring_frequency === 'monthly' ? 'Monthly' :
                                  customerDetail.recurring_frequency === 'bimonthly' ? 'Every 2 Months' :
                                  customerDetail.recurring_frequency === 'quarterly' ? 'Quarterly' :
                                  customerDetail.recurring_frequency === 'semiannually' ? 'Semi-annually' :
                                  `Every ${customerDetail.recurring_interval_days} days`
                                }
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Vehicles Section */}
                    <div className="detail-section">
                      <div className="detail-section__header">
                        <h4>Vehicles</h4>
                        <button
                          className="btn btn--primary btn--small"
                          onClick={() => setShowVehicleModal(true)}
                        >
                          + Add Vehicle
                        </button>
                      </div>
                      {customerDetail.vehicles?.length > 0 ? (
                        <div className="vehicle-list">
                          {customerDetail.vehicles.map((vehicle) => (
                            <div key={vehicle.id} className="vehicle-item">
                              <div className="vehicle-item__info">
                                <span className="vehicle-item__name">
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </span>
                                <span className="vehicle-item__details">
                                  {vehicle.color && `${vehicle.color}`}
                                  {vehicle.license_plate && ` - ${vehicle.license_plate}`}
                                </span>
                              </div>
                              <button
                                className="btn btn--danger btn--icon btn--small"
                                onClick={() => handleDeleteVehicle(vehicle.id)}
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-data">No vehicles added yet</p>
                      )}
                    </div>
                  </div>

                  {/* Saved Addresses Section */}
                  <div className="detail-section detail-section--full">
                    <div className="detail-section__header">
                      <h4>Saved Addresses</h4>
                      <button
                        className="btn btn--primary btn--small"
                        onClick={() => {
                          resetAddressForm();
                          setShowAddressModal(true);
                        }}
                      >
                        + Add Address
                      </button>
                    </div>
                    {customerDetail.addresses?.length > 0 ? (
                      <div className="address-list">
                        {customerDetail.addresses.map((addr) => (
                          <div key={addr.id} className="address-item">
                            <div className="address-item__info">
                              <span className="address-item__name">
                                {addr.name}
                                {addr.is_default === 1 && <span className="badge badge--primary ml-sm">Default</span>}
                              </span>
                              <span className="address-item__details">
                                {addr.address}{addr.city && `, ${addr.city}`}{addr.state && `, ${addr.state}`} {addr.zip}
                              </span>
                            </div>
                            <div className="address-item__actions">
                              <button
                                className="btn btn--outline btn--small"
                                title="Copy address"
                                onClick={() => copyAddress(buildAddressString(addr.address, addr.city, addr.state, addr.zip))}
                              >📋 Copy</button>
                              <button
                                className="btn btn--outline btn--small"
                                title="Open in Maps"
                                onClick={() => openInMaps(buildAddressString(addr.address, addr.city, addr.state, addr.zip))}
                              >🗺️ Maps</button>
                              {addr.is_default !== 1 && (
                                <button
                                  className="btn btn--outline btn--small"
                                  onClick={() => handleSetDefaultAddress(addr.id)}
                                >
                                  Set Default
                                </button>
                              )}
                              <button
                                className="btn btn--secondary btn--small"
                                onClick={() => handleEditAddress(addr)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn--danger btn--small"
                                onClick={() => handleDeleteAddress(addr.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-data">No saved addresses. Add addresses for quick selection when creating invoices.</p>
                    )}
                  </div>

                  {/* Recent Invoices Section */}
                  <div className="detail-section detail-section--full">
                    <div className="detail-section__header">
                      <h4>Recent Invoices</h4>
                    </div>
                    {customerDetail.recent_invoices?.length > 0 ? (
                      <div className="activity-list">
                        {customerDetail.recent_invoices.map((inv) => (
                          <div
                            key={inv.id}
                            className="activity-item activity-item--clickable"
                            onClick={() => navigate(`/invoices?open=${inv.id}`)}
                          >
                            <div className="activity-item__date">
                              {format(new Date(inv.created_at), 'MMM d, yyyy')}
                            </div>
                            <div className="activity-item__details">
                              <span className="activity-item__invoice-number">
                                {inv.is_quote ? `Quote ${inv.invoice_number}` : `Invoice #${inv.invoice_number}`}
                              </span>
                              <span className={`badge badge--${inv.status}`}>{inv.status}</span>
                            </div>
                            <div className="activity-item__amount">
                              ${Number(inv.total).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-data">No invoices yet</p>
                    )}
                  </div>

                  {/* Referrals Section */}
                  <div className="detail-section detail-section--full">
                    <div className="detail-section__header">
                      <h4>Referrals</h4>
                      <button
                        className="btn btn--primary btn--small"
                        onClick={() => setShowAddReferral(!showAddReferral)}
                      >
                        + Add Referral
                      </button>
                    </div>
                    {showAddReferral && (
                      <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                        <CustomerSearchSelect
                          customers={customers.filter(c => c.id !== customerDetail.id)}
                          value=""
                          onChange={(val) => { if (val) handleAddReferral(val); }}
                          placeholder="Type who they referred..."
                          required={false}
                        />
                      </div>
                    )}
                    {customerDetail.referred_by && (
                      <p className="no-data" style={{ marginBottom: '0.5rem' }}>
                        Referred by{' '}
                        <span
                          className="customer-name-link"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/customers/${customerDetail.referred_by.id}`)}
                        >
                          {customerDetail.referred_by.first_name} {customerDetail.referred_by.last_name}
                        </span>
                      </p>
                    )}
                    {(() => {
                      const booked = customerDetail.referrals?.filter(r => r.appointment_count > 0).length || 0;
                      return (
                        <div className={`referral-progress ${booked >= 4 ? 'referral-progress--ready' : ''}`}>
                          {booked >= 4
                            ? '🎁 Eligible for a free detail!'
                            : `${booked} of 4 referrals booked`}
                        </div>
                      );
                    })()}
                    {customerDetail.referrals?.length > 0 ? (
                      <div className="activity-list">
                        {customerDetail.referrals.map((r) => (
                          <div
                            key={r.id}
                            className="activity-item activity-item--clickable"
                            onClick={() => navigate(`/customers/${r.id}`)}
                          >
                            <div className="activity-item__details">
                              <span className="activity-item__invoice-number">
                                {r.first_name} {r.last_name}
                              </span>
                              <span className={`badge badge--${r.appointment_count > 0 ? 'paid' : 'draft'}`}>
                                {r.appointment_count > 0 ? 'Booked' : 'Not booked yet'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-data">No referrals yet</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Customer Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingCustomer(null); resetForm(); }}
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                name="first_name"
                className="form-input"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                name="last_name"
                className="form-input"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone *</label>
              <input
                type="tel"
                name="phone"
                className="form-input"
                placeholder="XXX-XXX-XXXX"
                value={formData.phone}
                onChange={handlePhoneChange}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input
              type="text"
              name="address"
              className="form-input"
              value={formData.address}
              onChange={handleChange}
            />
          </div>

          <div className="form-row form-row--3">
            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                name="city"
                className="form-input"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input
                type="text"
                name="state"
                className="form-input"
                value={formData.state}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input
                type="text"
                name="zip"
                className="form-input"
                value={formData.zip}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              name="notes"
              className="form-textarea"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Referred By</label>
            <CustomerSearchSelect
              customers={customers.filter(c => !editingCustomer || c.id !== editingCustomer.id)}
              value={formData.referred_by_customer_id}
              onChange={(val) => setFormData({ ...formData, referred_by_customer_id: val })}
              placeholder="Search for who referred them (optional)..."
              required={false}
            />
          </div>

          {/* Recurring Appointments Section */}
          <div className="recurring-section">
            <div className="recurring-section__header">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="recurring_enabled"
                  checked={formData.recurring_enabled}
                  onChange={handleCheckboxChange}
                />
                <span>Enable Recurring Appointments</span>
              </label>
              {editingCustomer && formData.recurring_enabled && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="recurring_paused"
                    checked={formData.recurring_paused}
                    onChange={handleCheckboxChange}
                  />
                  <span>Paused</span>
                </label>
              )}
            </div>

            {formData.recurring_enabled && (
              <div className="recurring-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Frequency</label>
                    <select
                      name="recurring_frequency"
                      className="form-select"
                      value={formData.recurring_frequency}
                      onChange={handleChange}
                    >
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                      <option value="bimonthly">Every 2 Months</option>
                      <option value="quarterly">Quarterly (3 months)</option>
                      <option value="semiannually">Semi-annually (6 months)</option>
                      <option value="custom">Custom Interval</option>
                    </select>
                  </div>
                  {formData.recurring_frequency === 'custom' && (
                    <div className="form-group">
                      <label className="form-label">Interval (Days)</label>
                      <input
                        type="number"
                        name="recurring_interval_days"
                        className="form-input"
                        value={formData.recurring_interval_days}
                        onChange={handleChange}
                        placeholder="e.g., 45"
                        min="1"
                      />
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Default Service</label>
                    <select
                      name="recurring_service_id"
                      className="form-select"
                      value={formData.recurring_service_id}
                      onChange={handleChange}
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name} - ${service.base_price_car}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      name="recurring_start_date"
                      className="form-input"
                      value={formData.recurring_start_date}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="recurring-info">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>Appointments will be auto-generated up to 7 days in advance</span>
                </div>
              </div>
            )}
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowModal(false); setEditingCustomer(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              {editingCustomer ? 'Save Changes' : 'Add Customer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Add Vehicle Modal */}
      <Modal
        isOpen={showVehicleModal}
        onClose={() => { setShowVehicleModal(false); resetVehicleForm(); }}
        title="Add Vehicle"
      >
        <form onSubmit={handleAddVehicle}>
          <div className="form-row form-row--3">
            <div className="form-group">
              <label className="form-label">Year</label>
              <input
                type="number"
                name="year"
                className="form-input"
                value={vehicleFormData.year}
                onChange={handleVehicleChange}
                placeholder="2024"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Make *</label>
              <input
                type="text"
                name="make"
                className="form-input"
                value={vehicleFormData.make}
                onChange={handleVehicleChange}
                placeholder="Toyota"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Model *</label>
              <input
                type="text"
                name="model"
                className="form-input"
                value={vehicleFormData.model}
                onChange={handleVehicleChange}
                placeholder="Camry"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Color</label>
              <input
                type="text"
                name="color"
                className="form-input"
                value={vehicleFormData.color}
                onChange={handleVehicleChange}
                placeholder="Black"
              />
            </div>
            <div className="form-group">
              <label className="form-label">License Plate</label>
              <input
                type="text"
                name="license_plate"
                className="form-input"
                value={vehicleFormData.license_plate}
                onChange={handleVehicleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Vehicle Type</label>
            <select
              name="vehicle_type"
              className="form-select"
              value={vehicleFormData.vehicle_type}
              onChange={handleVehicleChange}
            >
              <option value="sedan">Sedan</option>
              <option value="coupe">Coupe</option>
              <option value="hatchback">Hatchback</option>
              <option value="truck">Truck</option>
              <option value="suv">SUV</option>
              <option value="van">Van</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowVehicleModal(false); resetVehicleForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              Add Vehicle
            </button>
          </div>
        </form>
      </Modal>

      {/* Add/Edit Address Modal */}
      <Modal
        isOpen={showAddressModal}
        onClose={() => { setShowAddressModal(false); resetAddressForm(); }}
        title={editingAddress ? 'Edit Address' : 'Add Address'}
      >
        <form onSubmit={handleAddAddress}>
          <div className="form-group">
            <label className="form-label">Address Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Home, Work, Lake House"
              value={addressForm.name}
              onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Street Address *</label>
            <input
              type="text"
              className="form-input"
              value={addressForm.address}
              onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
              required
            />
          </div>

          <div className="form-row form-row--3">
            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.city}
                onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.state}
                onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                maxLength="2"
              />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.zip}
                onChange={(e) => setAddressForm({ ...addressForm, zip: e.target.value })}
                maxLength="10"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={addressForm.is_default}
                onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
              />
              <span>Set as default address</span>
            </label>
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowAddressModal(false); resetAddressForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              {editingAddress ? 'Save Changes' : 'Add Address'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Customers;
